import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';

import { listGroups, validateApiResponse } from '../../../services';
import { mapApiGroupToUi } from '../utils/groupMappers';

/**
 * Groups of the Groups tab. `search` filters by name/description on the (mock) service.
 * `isLoading` is derived: the last settled request is not the one for the current search/reload.
 *
 * @returns {{ groups: object[], isLoading: boolean, error: string|null, search: string,
 *   setSearch: (value: string) => void, reload: () => void }}
 */
export const useGroups = () => {
  const { t } = useTranslation('people-access');
  const [search, setSearch] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [settled, setSettled] = useState({ key: null, groups: [], error: null });
  const fetchIdRef = useRef(0);
  const requestKey = `${reloadToken}|${search}`;

  useEffect(() => {
    const fetchId = ++fetchIdRef.current;
    let mounted = true;
    const isCurrent = () => mounted && fetchId === fetchIdRef.current;

    listGroups({ search: { query: search, fields: ['name', 'description'] } })
      .then((res) => validateApiResponse(res, t('groups.list.loadError')))
      .then(({ body }) => {
        if (!isCurrent()) return;
        setSettled({ key: requestKey, groups: (body?.items ?? []).map(mapApiGroupToUi), error: null });
      })
      .catch((err) => {
        if (!isCurrent()) return;
        setSettled({
          key: requestKey,
          groups: [],
          error: err?.message ?? t('groups.list.loadError'),
        });
      });

    return () => {
      mounted = false;
    };
  }, [search, reloadToken, requestKey, t]);

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);
  const { groups, error } = settled;
  const isLoading = settled.key !== requestKey;

  return useMemo(
    () => ({ groups, isLoading, error, search, setSearch, reload }),
    [groups, isLoading, error, search, reload],
  );
};
